import { Type } from "@themes/pollinator/ui/Type";
import { SectionHeading } from "@themes/pollinator/ui/SectionHeading";
import {
  DonutChart,
  type DonutChartDataItem,
} from "@themes/pollinator/ui/DonutChart";

export type Props = {
  /** Section title, e.g. "Career stage". */
  title: string;
  /** Description shown under the title. */
  description?: string;
  /**
   * Org-side donut data. When omitted (below-threshold state) only the
   * program-wide donut is rendered, full-width.
   */
  orgData?: DonutChartDataItem[];
  /** Program-wide donut data — always shown. */
  programData: DonutChartDataItem[];
  /** Sample size for the org side, surfaced as "n = X". */
  orgN?: number;
  /** Sample size for the program side, surfaced as "n = X". */
  programN: number;
  /**
   * Label for the org column. Defaults to "Your org" so callers that
   * don't have the org name handy still render sensibly.
   */
  orgLabel?: string;
};

/**
 * One metric row in the Insights section. Renders the org's donut and the
 * Pollinator-wide donut side-by-side for visual comparison. Used for
 * single-select distributions where slices sum to 100% (career stage,
 * years practicing).
 *
 * When `orgData` is omitted (the 0 / 1-2 onboarded case), only the
 * program-wide donut renders.
 */
export function OrgInsightsDonutRow({
  title,
  description,
  orgData,
  programData,
  orgN,
  programN,
  orgLabel = "Your org",
}: Props) {
  return (
    <SectionHeading
      title={title}
      description={description}
      titleVariant="h4"
      descriptionVariant="body2"
    >
      <div className="flex flex-col gap-8 md:flex-row md:gap-16">
        {orgData && (
          <div className="flex flex-col items-start gap-2">
            <Type variant="caption" className="text-brand-700">
              {orgLabel}
              {orgN != null ? ` (n=${orgN})` : ""}
            </Type>
            <DonutChart
              data={orgData}
              size={180}
              showLegend
              showPercentage
              legendPosition="right"
            />
          </div>
        )}
        <div className="flex flex-col items-start gap-2">
          <Type variant="caption" className="text-brand-700">
            Pollinator overall (n={programN})
          </Type>
          <DonutChart
            data={programData}
            size={180}
            showLegend
            showPercentage
            legendPosition="right"
          />
        </div>
      </div>
    </SectionHeading>
  );
}

export default OrgInsightsDonutRow;
